import { Controller } from "@hotwired/stimulus"

// Manages client-side session timeout warnings and refreshes.
// Expects the following values:
// - remainingValue (Number): seconds remaining in the current session
// - warningValue (Number): how many seconds before expiry to show the warning modal
// - extendUrlValue (String): POST URL to extend the session
// - timeoutUrlValue (String): POST URL to perform timeout and get login redirect
export default class extends Controller {
  static targets = ["modal", "countdown"]

  static values = {
    remaining: Number,
    warning: Number,
    extendUrl: String,
    timeoutUrl: String
  }

  connect () {
    if (!this.hasRemainingValue || this.remainingValue <= 0) return

    this.expiryAt = new Date(Date.now() + this.remainingValue * 1000)
    this.startTimers()
  }

  disconnect () {
    this.clearTimers()
  }

  extendSession (event) {
    event.preventDefault()

    this.post(this.extendUrlValue)
      .then((data) => {
        if (!data || typeof data.remaining_seconds !== "number") return

        this.remainingValue = data.remaining_seconds
        this.expiryAt = new Date(Date.now() + this.remainingValue * 1000)

        this.hideModal()
        this.startTimers()
      })
      .catch(() => {
        // If the extension fails for any reason, fall back to timing out to login.
        this.timeoutSession()
      })
  }

  timeoutSession () {
    // If we have a timeout endpoint configured, call it so the server can
    // clear the session and tell us where to go next.
    if (this.hasTimeoutUrlValue) {
      this.post(this.timeoutUrlValue, { return_to: window.location.href })
        .then((data) => {
          if (data && data.redirect_url) {
            window.location = data.redirect_url
          } else {
            window.location.reload()
          }
        })
        .catch(() => {
          window.location.reload()
        })
    } else {
      window.location.reload()
    }
  }

  // Internal -----------------------------------------------------------------

  startTimers () {
    this.clearTimers()

    const now = Date.now()
    const remainingMs = Math.max(this.expiryAt - now, 0)
    const warningMs = Math.max(this.warningValue * 1000, 0)

    if (remainingMs === 0) {
      this.timeoutSession()
      return
    }

    if (remainingMs <= warningMs) {
      // We're already inside the warning window, show immediately.
      this.showModal()
    } else {
      const delayMs = remainingMs - warningMs
      this.warningTimeout = window.setTimeout(() => this.showModal(), delayMs)
    }

    this.expireTimeout = window.setTimeout(() => this.timeoutSession(), remainingMs)
  }

  clearTimers () {
    if (this.warningTimeout) {
      window.clearTimeout(this.warningTimeout)
      this.warningTimeout = null
    }

    if (this.expireTimeout) {
      window.clearTimeout(this.expireTimeout)
      this.expireTimeout = null
    }

    if (this.countdownInterval) {
      window.clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  showModal () {
    if (!this.hasModalTarget) return

    // Use Bootstrap's modal if present.
    if (window.$ && typeof window.$(this.modalTarget).modal === "function") {
      window.$(this.modalTarget).modal("show")
    } else {
      this.modalTarget.style.display = "block"
    }

    this.updateCountdown()
    this.countdownInterval = window.setInterval(() => this.updateCountdown(), 1000)
  }

  hideModal () {
    if (!this.hasModalTarget) return

    if (window.$ && typeof window.$(this.modalTarget).modal === "function") {
      window.$(this.modalTarget).modal("hide")
    } else {
      this.modalTarget.style.display = "none"
    }

    if (this.countdownInterval) {
      window.clearInterval(this.countdownInterval)
      this.countdownInterval = null
    }
  }

  updateCountdown () {
    if (!this.hasCountdownTarget) return

    const secondsLeft = Math.max(Math.round((this.expiryAt - Date.now()) / 1000), 0)

    if (secondsLeft <= 0) {
      this.countdownTarget.textContent = "0"
      return
    }

    this.countdownTarget.textContent = secondsLeft.toString()
  }

  post (url, body = {}) {
    const tokenElement = document.querySelector("meta[name=csrf-token]")
    const token = tokenElement && tokenElement.content

    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(token ? { "X-CSRF-Token": token } : {})
      },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then((response) => {
      if (!response.ok) throw new Error("Request failed")
      const contentType = response.headers.get("Content-Type") || ""
      if (contentType.includes("application/json")) {
        return response.json()
      }
      return {}
    })
  }
}

